import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/AuthContext';
import { useFactoryStore } from '@/core/hooks/useFactory';
import {
  fetchUserFactoryRoleId,
  fetchLocationAssignments,
  type LocationAssignment,
} from '@/core/api';

// Re-export for backwards compatibility
export type UserLocation = LocationAssignment;

/**
 * Fetches the current user's location assignments for the selected factory.
 * Used to filter content shown to operators.
 */
export const useUserLocations = () => {
  const { user } = useAuth();
  const { selectedFactoryId } = useFactoryStore();

  return useQuery({
    queryKey: ['user-locations', user?.id, selectedFactoryId],
    queryFn: async () => {
      if (!user || !selectedFactoryId) return [];

      // First get the user_factory_role id
      const userFactoryRoleId = await fetchUserFactoryRoleId(user.id, selectedFactoryId);

      if (!userFactoryRoleId) return [];

      // Get location assignments with related data
      return fetchLocationAssignments(userFactoryRoleId);
    },
    enabled: !!user && !!selectedFactoryId,
  });
};

/**
 * Returns the IDs of areas, lines, and stations the user is assigned to.
 */
export const useUserLocationIds = () => {
  const { data: locations = [] } = useUserLocations();

  const areaIds = locations
    .filter(l => l.area_id)
    .map(l => l.area_id as string);
  
  const lineIds = locations
    .filter(l => l.line_id)
    .map(l => l.line_id as string);
  
  const stationIds = locations
    .filter(l => l.station_id)
    .map(l => l.station_id as string);

  return {
    areaIds: [...new Set(areaIds)],
    lineIds: [...new Set(lineIds)],
    stationIds: [...new Set(stationIds)],
    hasLocations: locations.length > 0,
  };
};
